import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';

export default function GloboFlotante({ trigger, children, align = 'left', as = 'button' }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const [vShift, setVShift] = useState(0);
  const ref = useRef(null);
  const popRef = useRef(null);
  const isRightSide = align === 'right-side';

  const close = useCallback(() => {
    setOpen(false);
    setFlipped(false);
    setVShift(0);
  }, []);

  const toggle = useCallback(() => {
    if (ref.current) {
      const el = ref.current.firstElementChild || ref.current;
      const r = el.getBoundingClientRect();
      setAnchor({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
    }
    setOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inTrigger && !inPop) close();
    };
    const onScroll = () => close();
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !anchor || !popRef.current) return;
    const h = popRef.current.offsetHeight;
    if (isRightSide) {
      const mid = (anchor.top + anchor.bottom) / 2;
      const desired = mid - h / 2;
      const clamped = Math.max(8, Math.min(desired, window.innerHeight - h - 8));
      setVShift(clamped - desired);
      return;
    }
    if (anchor.bottom + 8 + h > window.innerHeight && anchor.top - 8 - h > 0) {
      setFlipped(true);
    }
  }, [open, anchor, isRightSide]);

  const isDiv = as === 'div';

  return (
    <div ref={ref} style={{ position: 'relative', display: isDiv ? 'block' : 'inline-flex' }}>
      {isDiv ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          style={{ cursor: 'pointer' }}
        >
          {trigger}
        </div>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'inline-flex',
          }}
        >
          {trigger}
        </button>
      )}
      {open && anchor && (
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            zIndex: 50,
            backgroundColor: '#141414',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '18px',
            padding: '12px',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            top: isRightSide ? (anchor.top + anchor.bottom) / 2 + vShift : flipped ? 'auto' : anchor.bottom + 8,
            bottom: isRightSide ? 'auto' : flipped ? window.innerHeight - anchor.top + 8 : 'auto',
            left: isRightSide
              ? anchor.right + 8
              : align === 'right'
              ? 'auto'
              : align === 'center'
              ? (anchor.left + anchor.right) / 2
              : anchor.left,
            right: isRightSide ? 'auto' : align === 'right' ? window.innerWidth - anchor.right : 'auto',
            transform: isRightSide
              ? 'translateY(-50%)'
              : align === 'center'
              ? 'translateX(-50%)'
              : undefined,
            maxWidth: 'min(320px, calc(100vw - 24px))',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
